import { BN } from '@velocity-exchange/sdk';
import { BASE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { capStringFractionDigits } from '@velocity-exchange/common/format';
import { TradeFormMessage } from 'src/@types/types';
import { useMemo } from 'react';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

interface UseSizeInputErrorMessageParams {
	baseSizeStringValue: string;
	sizeDigits: number;
	maxBaseAvailable: number;
	minTradeSizeForMarket: number;
	basePrecisionToUse?: BN;
	quoteSizeStringValue: string;
	marketSymbol: string | undefined;
}

/**
 * Hook that validates trade size inputs and returns appropriate error messages.
 *
 * Performs validation on trade size inputs to ensure they meet market requirements:
 * - Checks if trade size meets minimum requirements
 * - Validates trade size doesn't exceed maximum available
 * - Applies step size rounding with buffer to prevent rounding errors
 *
 * Returns error messages for display in the trade form when validation fails.
 * Uses a 0.5% buffer when checking maximum size to prevent rounding errors
 * from blocking legitimate max leverage trades.
 *
 * @returns TradeFormMessage object with error details, or undefined if no errors
 */
export const useSizeInputErrorMessage = ({
	baseSizeStringValue,
	sizeDigits,
	maxBaseAvailable,
	minTradeSizeForMarket,
	basePrecisionToUse,
	quoteSizeStringValue,
	marketSymbol,
}: UseSizeInputErrorMessageParams) => {
	const baseSize = parseInputBigNumOrZero(
		baseSizeStringValue,
		basePrecisionToUse || BASE_PRECISION_EXP
	);

	const roundedBaseSize = +capStringFractionDigits(
		baseSize.toNum().toString(),
		{ maxFractionDigits: sizeDigits }
	);
	const roundedMaxBaseAvailable = +capStringFractionDigits(
		maxBaseAvailable?.toString() || '0',
		{ maxFractionDigits: sizeDigits }
	);

	const roundedBaseSizeLessThanMinTradeSize =
		roundedBaseSize &&
		minTradeSizeForMarket !== undefined &&
		roundedBaseSize < minTradeSizeForMarket;

	const inputHasNonZeroQuoteSize = !!parseFloat(quoteSizeStringValue);

	const tradeSizeIsTooSmall =
		roundedBaseSizeLessThanMinTradeSize ||
		// If there's no base size, only show the error if the user has entered a
		// non-zero value for the quote size, as zero is obviously too small.
		(!roundedBaseSize && inputHasNonZeroQuoteSize);

	const tradeIsTooLarge =
		roundedBaseSize &&
		roundedMaxBaseAvailable &&
		roundedBaseSize * 0.995 > roundedMaxBaseAvailable; // 0.5% buffer to prevent rounding errors accidentally preventing the user from trading at max leverage

	return useMemo((): TradeFormMessage | undefined => {
		if (tradeSizeIsTooSmall) {
			return {
				messageTitle: '',
				shouldShowMessage: true,
				message: `Order size must be at least ${minTradeSizeForMarket} ${marketSymbol}`,
				messageType: 'error',
				skipMessageInConfirmationModal: true,
				disableTradeButton: true,
			};
		}

		if (tradeIsTooLarge) {
			return {
				messageTitle: '',
				shouldShowMessage: true,
				message: `Order size must be less than or equal to ${capStringFractionDigits(
					maxBaseAvailable.toString(),
					{ maxFractionDigits: sizeDigits }
				)} ${marketSymbol}`,
				messageType: 'error',
				skipMessageInConfirmationModal: true,
				disableTradeButton: true,
			};
		}
	}, [
		tradeSizeIsTooSmall,
		minTradeSizeForMarket,
		marketSymbol,
		tradeIsTooLarge,
		maxBaseAvailable,
		sizeDigits,
	]);
};
